// DocumentsPanel.jsx
// ─────────────────────────────────────────────────────────────
// 📄 HR Documents tab — 5 companies × 5 documents
//
// Companies: gvcloud | msoft | elonstaffing | stafferpay | clouddetox
// Documents: joining-letter | offer-letter | salary-slip
//            relieving-letter | experience-letter
//
// APIs (sab ek jaise — joining letter ka alag route nahi):
//   GET  /api/documents/companies                 → company + doc list
//   POST /api/documents/:docType/send             → PDF + email
//   POST /api/documents/:docType/preview          → sirf PDF (email nahi)
//
// Body me "company" field hi decide karti hai kaunsa letterhead lagega.
// Nayi company backend me add hone par ye file badalni NAHI padti —
// list API se aati hai. FALLBACK_COMPANIES sirf tab chalti hai jab
// API reachable na ho.
//
// ⭐ NEW: Experience Letter me designation likhte hi experience points
// auto-fill ho jate hain (experienceTemplates.js se). Naya role add
// karna ho to sirf wahi file edit karo — ye file chhune ki zarurat nahi.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useRef } from "react";
import {
  EXPERIENCE_TEMPLATES,
  matchTemplate,
  templateText,
  isUntouchedTemplateText,
  templateByKey,
} from "./experienceTemplates";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/+$/, "");

// Server se list na aaye to ye fallback chalega
const FALLBACK_COMPANIES = [
  { key: "gvcloud", display: "GvCloud Secure", themeColor: "#1e2a4a" },
  { key: "msoft", display: "Msoft", themeColor: "#0573fa" },
  { key: "elonstaffing", display: "Elon Staffing", themeColor: "#5270fe" },
  { key: "stafferpay", display: "Stafferpay", themeColor: "#1c509a" },
  { key: "clouddetox", display: "Cloud Detox", themeColor: "#0ea5a4" },
];

const ACCENT = "#ff5421"; // admin panel ka apna accent

const DOC_TYPES = [
  {
    key: "joining-letter",
    icon: "🤝",
    title: "Joining Letter",
    desc: "Welcome letter for new joinee",
  },
  {
    key: "offer-letter",
    icon: "📄",
    title: "Offer Letter",
    desc: "Appointment letter + terms + pay slip page",
  },
  {
    key: "salary-slip",
    icon: "💰",
    title: "Salary Slip",
    desc: "Monthly pay slip with net salary in words",
  },
  {
    key: "relieving-letter",
    icon: "📋",
    title: "Relieving Letter",
    desc: "Resignation acknowledge + relieving order",
  },
  {
    key: "experience-letter",
    icon: "🏆",
    title: "Experience Letter",
    desc: "Tenure + designation + work summary",
  },
];

// Har doc type ka initial form
const INITIAL_FORMS = {
   "joining-letter": {
    name: "", email: "", designation: "", dateOfJoining: "",
    department: "", empId: "",
    responsibilities: "",
    // pay slip page (khali chhodo to page banega hi nahi)
    month: "", salaryMode: "components",
    basic: "", hra: "", city: "", special: "", ctcLakhs: "",
    pf: "", gratuity: "", lta: "", medical: "", bonus: "",
    tax: "", conveyance: "", misc: "",
  },
  "offer-letter": {
    name: "", email: "", designation: "", dateOfJoining: "",
    ctcLakhs: "", location: "",
  },
  "salary-slip": {
    name: "", email: "", designation: "", empId: "", month: "",
    salaryMode: "components", // "components" | "ctc"
    basic: "", hra: "", city: "", special: "", ctcLakhs: "",
    pf: "", gratuity: "", lta: "", medical: "", bonus: "",
    tax: "", conveyance: "", misc: "",
  },
  "relieving-letter": {
    name: "", email: "", empId: "",
    resignationDate: "", tenureFrom: "", tenureTo: "",
  },
  "experience-letter": {
    name: "", email: "", empId: "", designation: "",
    tenureFrom: "", tenureTo: "", gender: "male",
    experiencePoints: "",
  },
};

const INITIAL_ADVANCED = { issueDate: "", signatoryName: "", signatoryRole: "" };

export default function DocumentsPanel({ adminKey }) {
  const [companies, setCompanies] = useState(FALLBACK_COMPANIES);
  const [company, setCompany] = useState("gvcloud");
  const [docType, setDocType] = useState("joining-letter");

  const [forms, setForms] = useState(INITIAL_FORMS);
  const [advanced, setAdvanced] = useState(INITIAL_ADVANCED);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null); // { ok, msg }
  const [previewUrl, setPreviewUrl] = useState(null);

  const previewUrlRef = useRef(null);
  const form = forms[docType];
  const activeDoc = DOC_TYPES.find((d) => d.key === docType);
  const activeCompany = companies.find((c) => c.key === company) || companies[0];

  // Active company ka brand colour — na ho to admin accent
  const brand = activeCompany?.themeColor || ACCENT;

  // ── Server se company list uthao ──────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/documents/companies`, {
          headers: adminKey ? { "x-admin-key": adminKey } : {},
        });
        const data = await res.json();
        if (!cancelled && data.success && Array.isArray(data.companies) && data.companies.length) {
          setCompanies(data.companies);
          if (!data.companies.some((c) => c.key === company)) {
            setCompany(data.companies[0].key);
          }
        }
      } catch {
        // chup-chaap fallback list use karo
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey]);

  // ── Object URL cleanup (memory leak se bachne ke liye) ─
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  function setField(field, value) {
    setForms((prev) => {
      const next = { ...prev[docType], [field]: value };

      // ⭐ Experience letter: designation likhte hi points auto-fill.
      // Agar user ne points khud edit kar liye hain to overwrite nahi hoga.
      if (docType === "experience-letter" && field === "designation") {
        if (isUntouchedTemplateText(prev[docType].experiencePoints)) {
          const tpl = matchTemplate(value);
          next.experiencePoints = tpl ? templateText(tpl) : "";
        }
      }

      return { ...prev, [docType]: next };
    });
    setResult(null);
  }

  // Quick template chip se points + designation dono set
  function applyTemplate(key) {
    const tpl = templateByKey(key);
    if (!tpl) return;
    setForms((prev) => ({
      ...prev,
      "experience-letter": {
        ...prev["experience-letter"],
        designation: prev["experience-letter"].designation.trim() || tpl.label,
        experiencePoints: templateText(tpl),
      },
    }));
    setResult(null);
  }

  function switchDoc(key) {
    setDocType(key);
    setResult(null);
  }

  function switchCompany(key) {
    setCompany(key);
    setResult(null);
  }

  function resetForm() {
    setForms((prev) => ({ ...prev, [docType]: INITIAL_FORMS[docType] }));
    setAdvanced(INITIAL_ADVANCED);
    setResult(null);
  }

  function closePreview() {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  }

  // ── Payload builder ──────────────────────────────────
  function buildPayload() {
    const f = form;
    const num = (v) => (v === "" || v == null ? undefined : Number(v));

    const payload = {
      company,                       // ⭐ letterhead isi se decide hota hai
      name: f.name.trim(),
    };

    // Preview me email optional hai — khali ho to bhejo hi mat
    if (f.email.trim()) payload.email = f.email.trim();

    // Optional — khali ho to bheja hi nahi jayega (server default lagayega)
    if (advanced.issueDate) payload.issueDate = advanced.issueDate;
    if (advanced.signatoryName.trim()) payload.signatoryName = advanced.signatoryName.trim();
    if (advanced.signatoryRole.trim()) payload.signatoryRole = advanced.signatoryRole.trim();

  if (docType === "joining-letter") {
      payload.designation = f.designation.trim();
      payload.dateOfJoining = f.dateOfJoining;
      if (f.department.trim()) payload.department = f.department.trim();
      if (f.empId.trim()) payload.empId = f.empId.trim();

      // page 2 — khali = page skip
      const resp = f.responsibilities.split("\n").map((l) => l.trim()).filter(Boolean);
      if (resp.length) payload.responsibilities = resp;

      // page 3 — khali = page skip
      if (f.month.trim()) payload.month = f.month.trim();
      if (f.salaryMode === "ctc") {
        if (f.ctcLakhs !== "") payload.ctcLakhs = num(f.ctcLakhs);
      } else if (f.basic !== "") {
        payload.basic = num(f.basic) ?? 0;
        payload.hra = num(f.hra) ?? 0;
        payload.city = num(f.city) ?? 0;
        payload.special = num(f.special) ?? 0;
      }
      ["pf", "gratuity", "lta", "medical", "bonus", "tax", "conveyance", "misc"].forEach((k) => {
        if (f[k] !== "") payload[k] = Number(f[k]);
      });
      return payload;
    }

    if (docType === "offer-letter") {
      payload.designation = f.designation.trim();
      payload.dateOfJoining = f.dateOfJoining;
      payload.ctcLakhs = num(f.ctcLakhs);
      if (f.location.trim()) payload.location = f.location.trim();
      return payload;
    }

    if (docType === "salary-slip") {
      payload.designation = f.designation.trim();
      payload.empId = f.empId.trim();
      payload.month = f.month.trim();

      if (f.salaryMode === "ctc") {
        payload.ctcLakhs = num(f.ctcLakhs);
      } else {
        payload.basic = num(f.basic) ?? 0;
        payload.hra = num(f.hra) ?? 0;
        payload.city = num(f.city) ?? 0;
        payload.special = num(f.special) ?? 0;
      }

      // sirf filled fields bhejo — khali chhodoge to slip me "NA" aayega
      ["pf", "gratuity", "lta", "medical", "bonus", "tax", "conveyance", "misc"].forEach((k) => {
        if (f[k] !== "") payload[k] = Number(f[k]);
      });
      return payload;
    }

    if (docType === "relieving-letter") {
      payload.empId = f.empId.trim();
      payload.resignationDate = f.resignationDate;
      payload.tenureFrom = f.tenureFrom;
      payload.tenureTo = f.tenureTo;
      return payload;
    }

    // experience-letter
    payload.empId = f.empId.trim();
    payload.designation = f.designation.trim();
    payload.tenureFrom = f.tenureFrom;
    payload.tenureTo = f.tenureTo;
    payload.gender = f.gender;
    const points = f.experiencePoints.split("\n").map((l) => l.trim()).filter(Boolean);
    if (points.length) payload.experiencePoints = points;
    return payload;
  }

  // ── Validation ───────────────────────────────────────
  function validate({ needEmail = true } = {}) {
    const f = form;
    const has = (v) => v != null && String(v).trim() !== "";

    if (!has(f.name)) return "Employee Name required hai";
    if (needEmail) {
      if (!has(f.email)) return "Email required hai";
      if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) return "Email sahi format me nahi hai";
    } else if (has(f.email) && !/^\S+@\S+\.\S+$/.test(f.email.trim())) {
      return "Email sahi format me nahi hai";
    }

    if (docType === "joining-letter") {
      if (!has(f.designation) || !has(f.dateOfJoining))
        return "Designation aur Date of Joining required hain";
    }
    if (docType === "offer-letter") {
      if (!has(f.designation) || !has(f.dateOfJoining) || !has(f.ctcLakhs))
        return "Designation, Date of Joining, CTC — sab required hain";
      if (Number(f.ctcLakhs) <= 0) return "CTC 0 se zyada hona chahiye";
    }
    if (docType === "salary-slip") {
      if (!has(f.designation) || !has(f.empId) || !has(f.month))
        return "Designation, Emp ID, Month — sab required hain";
      if (f.salaryMode === "ctc" && !has(f.ctcLakhs)) return "CTC (Lakhs) required hai";
      if (f.salaryMode === "components" && !has(f.basic)) return "Basic salary required hai";
    }
    if (docType === "relieving-letter") {
      if (!has(f.empId) || !has(f.resignationDate) || !has(f.tenureFrom) || !has(f.tenureTo))
        return "Emp ID, Resignation Date, Tenure From/To — sab required hain";
    }
    if (docType === "experience-letter") {
      if (!has(f.empId) || !has(f.designation) || !has(f.tenureFrom) || !has(f.tenureTo))
        return "Emp ID, Designation, Tenure From/To — sab required hain";
    }

    // tenure sanity check
    if (f.tenureFrom && f.tenureTo && f.tenureFrom > f.tenureTo)
      return "Tenure From, Tenure To se baad ka nahi ho sakta";

    return null;
  }

  function headers() {
    return {
      "Content-Type": "application/json",
      ...(adminKey ? { "x-admin-key": adminKey } : {}),
    };
  }

  // ── Preview (PDF, email nahi) ─────────────────────────
  async function handlePreview() {
    const err = validate({ needEmail: false });
    if (err) return setResult({ ok: false, msg: err });

    setPreviewing(true);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/documents/${docType}/preview`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(buildPayload()),
      });

      if (!res.ok) {
        let msg = `Preview fail (HTTP ${res.status})`;
        try {
          const data = await res.json();
          msg = data.message || msg;
        } catch { /* PDF nahi, JSON bhi nahi */ }
        setResult({ ok: false, msg });
        return;
      }

      const blob = await res.blob();
      closePreview();
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch {
      setResult({ ok: false, msg: "Server se connect nahi ho pa raha" });
    } finally {
      setPreviewing(false);
    }
  }

  // ── HTML preview — letterhead/CSS debug karne ke liye ──
  async function handleHtmlPreview() {
    const err = validate({ needEmail: false });
    if (err) return setResult({ ok: false, msg: err });

    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/documents/${docType}/preview.html`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(buildPayload()),
      });
      const html = await res.text();
      const tab = window.open("", "_blank");
      if (!tab) {
        setResult({ ok: false, msg: "Popup block ho gaya — browser me allow karo" });
        return;
      }
      tab.document.write(html);
      tab.document.close();
    } catch {
      setResult({ ok: false, msg: "Server se connect nahi ho pa raha" });
    }
  }

  // ── Send (PDF + email) ────────────────────────────────
  async function handleSend() {
    const err = validate();
    if (err) return setResult({ ok: false, msg: err });

    const ok = window.confirm(
      `${activeDoc.title} — ${activeCompany.display}\n\n` +
      `${form.email} par bhejein?`
    );
    if (!ok) return;

    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/documents/${docType}/send`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setResult({ ok: true, msg: data.message || "Bhej diya gaya!" });
      } else {
        setResult({ ok: false, msg: data.message || `Send fail ho gaya (HTTP ${res.status})` });
      }
    } catch {
      setResult({ ok: false, msg: "Server se connect nahi ho pa raha" });
    } finally {
      setSending(false);
    }
  }

  const busy = sending || previewing;

  return (
    <div>
      {/* ── Company selector — sabse pehle ── */}
      <div style={s.companyBar}>
        <div style={s.companyLabel}>🏢 Company</div>
        <div style={s.companyBtns}>
          {companies.map((c) => {
            const isActive = company === c.key;
            const color = c.themeColor || ACCENT;
            return (
              <button
                key={c.key}
                style={{
                  ...s.companyBtn,
                  ...(isActive
                    ? { background: color, color: "#fff", border: `1.5px solid ${color}` }
                    : {}),
                }}
                onClick={() => switchCompany(c.key)}
                disabled={busy}
              >
                <span
                  style={{
                    ...s.companyDot,
                    background: isActive ? "rgba(255,255,255,.85)" : color,
                  }}
                />
                {c.display}
              </button>
            );
          })}
        </div>
        <div style={s.companyHint}>
          Letterhead, logo, footer aur signature isi company ka lagega
        </div>
      </div>

      {/* ── Document type cards ── */}
      <div style={s.cardRow}>
        {DOC_TYPES.map((d) => (
          <button
            key={d.key}
            style={{ ...s.docCard, ...(docType === d.key ? s.docCardActive : {}) }}
            onClick={() => switchDoc(d.key)}
            disabled={busy}
          >
            <div style={s.docIcon}>{d.icon}</div>
            <div style={{ ...s.docTitle, ...(docType === d.key ? { color: "#fff" } : {}) }}>
              {d.title}
            </div>
            <div style={{ ...s.docDesc, ...(docType === d.key ? { color: "rgba(255,255,255,.85)" } : {}) }}>
              {d.desc}
            </div>
          </button>
        ))}
      </div>

      {/* ── Form card ── */}
      <div style={{ ...s.formCard, borderTop: `3px solid ${brand}` }}>
        <div style={s.formHeader}>
          <span style={{ fontSize: 22 }}>{activeDoc.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={s.formTitle}>{activeDoc.title}</div>
            <div style={s.formSub}>
              PDF generate hoke email par attach ho jayega
            </div>
          </div>
          <span style={{ ...s.companyChip, background: brand }}>
            {activeCompany.display}
          </span>
        </div>

        {/* ── Common fields ── */}
        <div style={s.grid}>
          <Field label="Employee Name *">
            <input style={s.input} placeholder="e.g. Kapil Kumar"
              value={form.name} onChange={(e) => setField("name", e.target.value)} />
          </Field>
          <Field label="Email *">
            <input style={s.input} type="email" placeholder="employee@email.com"
              value={form.email} onChange={(e) => setField("email", e.target.value)} />
          </Field>
        </div>

        {/* ══════════ JOINING LETTER ══════════ */}
    {docType === "joining-letter" && (
          <>
            <div style={s.grid}>
              <Field label="Designation *">
                <input style={s.input} placeholder="e.g. Data Analyst"
                  value={form.designation} onChange={(e) => setField("designation", e.target.value)} />
              </Field>
              <Field label="Date of Joining *">
                <input style={s.input} type="date"
                  value={form.dateOfJoining} onChange={(e) => setField("dateOfJoining", e.target.value)} />
              </Field>
            </div>
            <div style={s.grid}>
              <Field label="Department" hint="Optional">
                <input style={s.input} placeholder="e.g. IT"
                  value={form.department} onChange={(e) => setField("department", e.target.value)} />
              </Field>
              <Field label="Emp ID" hint="Page 2/3 ke header me chhapega">
                <input style={s.input} placeholder="e.g. GV-228195"
                  value={form.empId} onChange={(e) => setField("empId", e.target.value)} />
              </Field>
            </div>

            {/* ══ PAGE 2 — Responsibilities ══ */}
            <div style={s.sectionLabel}>Page 2 — Roles &amp; Responsibilities</div>
            <Field
              label="Responsibilities"
              hint="Har line ek bullet. Khali chhodoge to ye page banega hi nahi."
            >
              <textarea
                style={{ ...s.input, minHeight: 110, resize: "vertical", lineHeight: 1.6 }}
                placeholder={"Manage the entire life cycle of the recruitment process for clients in the US.\nWork closely with the Recruiting leads and hiring managers.\nWork on strategic global hiring programs."}
                value={form.responsibilities}
                onChange={(e) => setField("responsibilities", e.target.value)}
              />
            </Field>

            {/* ══ PAGE 3 — Pay Slip ══ */}
            <div style={s.sectionLabel}>Page 3 — Pay Slip (khali = page skip)</div>
            <div style={s.grid}>
              <Field label="Month" hint="Khali = joining date ka mahina">
                <input style={s.input} placeholder="e.g. October 2025"
                  value={form.month} onChange={(e) => setField("month", e.target.value)} />
              </Field>
              <Field label="Salary Input Mode">
                <div style={s.toggleRow}>
                  {[
                    { v: "components", label: "Manual Breakup" },
                    { v: "ctc", label: "CTC se Auto" },
                  ].map((m) => (
                    <button key={m.v}
                      style={{ ...s.toggleBtn, ...(form.salaryMode === m.v ? s.toggleBtnActive : {}) }}
                      onClick={() => setField("salaryMode", m.v)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            {form.salaryMode === "ctc" ? (
              <div style={s.grid}>
                <Field label="CTC (Lakhs/Year)" hint="Basic 60%, HRA 40% of basic, baaki City/Special">
                  <input style={s.input} type="number" step="0.01" placeholder="4.5"
                    value={form.ctcLakhs} onChange={(e) => setField("ctcLakhs", e.target.value)} />
                </Field>
                <div style={{ flex: 1 }} />
              </div>
            ) : (
              <div style={s.grid4}>
                <Field label="Basic">
                  <input style={s.input} type="number" placeholder="22500"
                    value={form.basic} onChange={(e) => setField("basic", e.target.value)} />
                </Field>
                <Field label="HRA">
                  <input style={s.input} type="number" placeholder="9000"
                    value={form.hra} onChange={(e) => setField("hra", e.target.value)} />
                </Field>
                <Field label="City All.">
                  <input style={s.input} type="number" placeholder="3000"
                    value={form.city} onChange={(e) => setField("city", e.target.value)} />
                </Field>
                <Field label="Special All.">
                  <input style={s.input} type="number" placeholder="3000"
                    value={form.special} onChange={(e) => setField("special", e.target.value)} />
                </Field>
              </div>
            )}

            <div style={s.grid4}>
              <Field label="Provident Fund">
                <input style={s.input} type="number" placeholder="1800"
                  value={form.pf} onChange={(e) => setField("pf", e.target.value)} />
              </Field>
              <Field label="Gratuity">
                <input style={s.input} type="number"
                  value={form.gratuity} onChange={(e) => setField("gratuity", e.target.value)} />
              </Field>
              <Field label="Leave Travel">
                <input style={s.input} type="number"
                  value={form.lta} onChange={(e) => setField("lta", e.target.value)} />
              </Field>
              <Field label="Medical">
                <input style={s.input} type="number"
                  value={form.medical} onChange={(e) => setField("medical", e.target.value)} />
              </Field>
            </div>
            <div style={s.grid4}>
              <Field label="Bonus All.">
                <input style={s.input} type="number"
                  value={form.bonus} onChange={(e) => setField("bonus", e.target.value)} />
              </Field>
              <Field label="Tax">
                <input style={s.input} type="number"
                  value={form.tax} onChange={(e) => setField("tax", e.target.value)} />
              </Field>
              <Field label="Conveyance">
                <input style={s.input} type="number"
                  value={form.conveyance} onChange={(e) => setField("conveyance", e.target.value)} />
              </Field>
              <Field label="Misc.">
                <input style={s.input} type="number"
                  value={form.misc} onChange={(e) => setField("misc", e.target.value)} />
              </Field>
            </div>
          </>
        )}

        {/* ══════════ OFFER LETTER ══════════ */}
        {docType === "offer-letter" && (
          <>
            <div style={s.grid}>
              <Field label="Designation *">
                <input style={s.input} placeholder="e.g. Data Scientist"
                  value={form.designation} onChange={(e) => setField("designation", e.target.value)} />
              </Field>
              <Field label="Date of Joining *">
                <input style={s.input} type="date"
                  value={form.dateOfJoining} onChange={(e) => setField("dateOfJoining", e.target.value)} />
              </Field>
            </div>
            <div style={s.grid}>
              <Field label="CTC (Lakhs/Year) *" hint="e.g. 4.5 → Basic/HRA/City/Special auto ban jayenge">
                <input style={s.input} type="number" step="0.01" placeholder="4.5"
                  value={form.ctcLakhs} onChange={(e) => setField("ctcLakhs", e.target.value)} />
              </Field>
              <Field label="Location" hint="Khali chhodo to company ka default sheher">
                <input style={s.input} placeholder="Noida"
                  value={form.location} onChange={(e) => setField("location", e.target.value)} />
              </Field>
            </div>
          </>
        )}

        {/* ══════════ SALARY SLIP ══════════ */}
        {docType === "salary-slip" && (
          <>
            <div style={s.grid}>
              <Field label="Designation *">
                <input style={s.input} placeholder="e.g. QA Engineer"
                  value={form.designation} onChange={(e) => setField("designation", e.target.value)} />
              </Field>
              <Field label="Emp ID *">
                <input style={s.input} placeholder="e.g. GV-228195"
                  value={form.empId} onChange={(e) => setField("empId", e.target.value)} />
              </Field>
            </div>
            <div style={s.grid}>
              <Field label="Month *" hint="Jaisa letter me chhapna hai">
                <input style={s.input} placeholder="e.g. March 2026"
                  value={form.month} onChange={(e) => setField("month", e.target.value)} />
              </Field>
              <Field label="Salary Input Mode">
                <div style={s.toggleRow}>
                  {[
                    { v: "components", label: "Manual Breakup" },
                    { v: "ctc", label: "CTC se Auto" },
                  ].map((m) => (
                    <button key={m.v}
                      style={{ ...s.toggleBtn, ...(form.salaryMode === m.v ? s.toggleBtnActive : {}) }}
                      onClick={() => setField("salaryMode", m.v)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            {form.salaryMode === "ctc" ? (
              <div style={s.grid}>
                <Field label="CTC (Lakhs/Year) *" hint="Basic 60%, HRA 40% of basic, baaki City/Special">
                  <input style={s.input} type="number" step="0.01" placeholder="4.5"
                    value={form.ctcLakhs} onChange={(e) => setField("ctcLakhs", e.target.value)} />
                </Field>
                <div style={{ flex: 1 }} />
              </div>
            ) : (
              <div style={s.grid4}>
                <Field label="Basic *">
                  <input style={s.input} type="number" placeholder="22500"
                    value={form.basic} onChange={(e) => setField("basic", e.target.value)} />
                </Field>
                <Field label="HRA">
                  <input style={s.input} type="number" placeholder="9000"
                    value={form.hra} onChange={(e) => setField("hra", e.target.value)} />
                </Field>
                <Field label="City All.">
                  <input style={s.input} type="number" placeholder="3000"
                    value={form.city} onChange={(e) => setField("city", e.target.value)} />
                </Field>
                <Field label="Special All.">
                  <input style={s.input} type="number" placeholder="3000"
                    value={form.special} onChange={(e) => setField("special", e.target.value)} />
                </Field>
              </div>
            )}

            <div style={s.sectionLabel}>Reference / Reimbursement (khali = NA)</div>
            <div style={s.grid4}>
              <Field label="Provident Fund">
                <input style={s.input} type="number" placeholder="1800"
                  value={form.pf} onChange={(e) => setField("pf", e.target.value)} />
              </Field>
              <Field label="Gratuity">
                <input style={s.input} type="number" placeholder="0"
                  value={form.gratuity} onChange={(e) => setField("gratuity", e.target.value)} />
              </Field>
              <Field label="Leave Travel">
                <input style={s.input} type="number" placeholder="0"
                  value={form.lta} onChange={(e) => setField("lta", e.target.value)} />
              </Field>
              <Field label="Medical">
                <input style={s.input} type="number" placeholder="0"
                  value={form.medical} onChange={(e) => setField("medical", e.target.value)} />
              </Field>
            </div>

            <div style={s.sectionLabel}>Deductions (khali = NA)</div>
            <div style={s.grid4}>
              <Field label="Bonus All.">
                <input style={s.input} type="number" placeholder="0"
                  value={form.bonus} onChange={(e) => setField("bonus", e.target.value)} />
              </Field>
              <Field label="Tax">
                <input style={s.input} type="number" placeholder="0"
                  value={form.tax} onChange={(e) => setField("tax", e.target.value)} />
              </Field>
              <Field label="Conveyance">
                <input style={s.input} type="number" placeholder="0"
                  value={form.conveyance} onChange={(e) => setField("conveyance", e.target.value)} />
              </Field>
              <Field label="Misc.">
                <input style={s.input} type="number" placeholder="0"
                  value={form.misc} onChange={(e) => setField("misc", e.target.value)} />
              </Field>
            </div>
          </>
        )}

        {/* ══════════ RELIEVING LETTER ══════════ */}
        {docType === "relieving-letter" && (
          <>
            <div style={s.grid}>
              <Field label="Emp ID *">
                <input style={s.input} placeholder="e.g. GV-228204"
                  value={form.empId} onChange={(e) => setField("empId", e.target.value)} />
              </Field>
              <Field label="Resignation Date *">
                <input style={s.input} type="date"
                  value={form.resignationDate} onChange={(e) => setField("resignationDate", e.target.value)} />
              </Field>
            </div>
            <div style={s.grid}>
              <Field label="Tenure From *">
                <input style={s.input} type="date"
                  value={form.tenureFrom} onChange={(e) => setField("tenureFrom", e.target.value)} />
              </Field>
              <Field label="Tenure To (Relieving Date) *">
                <input style={s.input} type="date"
                  value={form.tenureTo} onChange={(e) => setField("tenureTo", e.target.value)} />
              </Field>
            </div>
          </>
        )}

        {/* ══════════ EXPERIENCE LETTER ══════════ */}
        {docType === "experience-letter" && (
          <>
            <div style={s.grid}>
              <Field label="Emp ID *">
                <input style={s.input} placeholder="e.g. GV-228204"
                  value={form.empId} onChange={(e) => setField("empId", e.target.value)} />
              </Field>
              <Field
                label="Designation (at leaving) *"
                hint="Role likhte hi neeche ke points apne aap bhar jayenge"
              >
                <input style={s.input} placeholder="e.g. QA Tester"
                  value={form.designation} onChange={(e) => setField("designation", e.target.value)} />
              </Field>
            </div>
            <div style={s.grid}>
              <Field label="Tenure From *">
                <input style={s.input} type="date"
                  value={form.tenureFrom} onChange={(e) => setField("tenureFrom", e.target.value)} />
              </Field>
              <Field label="Tenure To *">
                <input style={s.input} type="date"
                  value={form.tenureTo} onChange={(e) => setField("tenureTo", e.target.value)} />
              </Field>
            </div>
            <div style={s.grid}>
              <Field label="Gender" hint="Letter me his/him ya her use hoga">
                <div style={s.toggleRow}>
                  {[
                    { v: "male", label: "Male (his/him)" },
                    { v: "female", label: "Female (her)" },
                  ].map((g) => (
                    <button key={g.v}
                      style={{ ...s.toggleBtn, ...(form.gender === g.v ? s.toggleBtnActive : {}) }}
                      onClick={() => setField("gender", g.v)}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </Field>
              <div style={{ flex: 1 }} />
            </div>

            {/* ── Quick templates — ek click me points ── */}
            <div style={s.sectionLabel}>Quick Templates</div>
            <div style={s.tplRow}>
              {EXPERIENCE_TEMPLATES.map((t) => {
                const active = templateText(t) === form.experiencePoints;
                return (
                  <button
                    key={t.key}
                    type="button"
                    style={{
                      ...s.tplChip,
                      ...(active
                        ? { background: brand, color: "#fff", border: `1.5px solid ${brand}` }
                        : {}),
                    }}
                    onClick={() => applyTemplate(t.key)}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            <Field
              label="Experience Points"
              hint="Har line ek bullet point banegi. Edit karoge to auto-fill ruk jayega — tumhara text safe rahega."
            >
              <textarea
                style={{ ...s.input, minHeight: 110, resize: "vertical", lineHeight: 1.6 }}
                placeholder={"Processed and analyzed data using Python libraries like NumPy and Pandas.\nDesigned interactive dashboards in Power BI and Tableau."}
                value={form.experiencePoints}
                onChange={(e) => setField("experiencePoints", e.target.value)}
              />
            </Field>
          </>
        )}

        {/* ── Advanced (optional) ── */}
        <button style={s.advToggle} onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? "▾" : "▸"} Advanced (optional) — Issue Date, Signatory
        </button>

        {showAdvanced && (
          <div style={s.advBox}>
            <div style={s.grid}>
              <Field label="Issue Date" hint="Khali = aaj ki date">
                <input style={s.input} type="date"
                  value={advanced.issueDate}
                  onChange={(e) => setAdvanced({ ...advanced, issueDate: e.target.value })} />
              </Field>
              <Field label="Signatory Name" hint="Khali = company ka default">
                <input style={s.input} placeholder="Neha Sharma"
                  value={advanced.signatoryName}
                  onChange={(e) => setAdvanced({ ...advanced, signatoryName: e.target.value })} />
              </Field>
              <Field label="Signatory Role">
                <input style={s.input} placeholder="HR. Manager"
                  value={advanced.signatoryRole}
                  onChange={(e) => setAdvanced({ ...advanced, signatoryRole: e.target.value })} />
              </Field>
            </div>
          </div>
        )}

        {/* ── Result banner ── */}
        {result && (
          <div style={{ ...s.resultBanner, ...(result.ok ? s.resultOk : s.resultErr) }}>
            {result.ok ? "✅" : "❌"} {result.msg}
          </div>
        )}

        {/* ── Actions ── */}
        <div style={s.actionRow}>
          <button style={s.btnReset} onClick={resetForm} disabled={busy}>
            Reset
          </button>
          <button style={s.btnHtml} onClick={handleHtmlPreview} disabled={busy}>
            🧩 HTML
          </button>
          <button style={{ ...s.btnPreview, color: brand, border: `1.5px solid ${brand}`, ...(previewing ? { opacity: 0.7 } : {}) }}
            onClick={handlePreview} disabled={busy}>
            {previewing ? "⏳ Banaya ja raha hai..." : "👁 Preview PDF"}
          </button>
          <button style={{ ...s.btnSend, background: brand, ...(sending ? { opacity: 0.7 } : {}) }}
            onClick={handleSend} disabled={busy}>
            {sending ? "⏳ Generating & sending..." : `📧 Send ${activeDoc.title}`}
          </button>
        </div>

        <div style={s.tip}>
          💡 Mail bhejne se pehle <b>Preview PDF</b> dabao — design check ho jayega,
          employee ko mail nahi jayega. Letterhead/CSS toota lage to <b>HTML</b> se
          browser me kholo.
        </div>
      </div>

      {/* ── PDF Preview Modal ── */}
      {previewUrl && (
        <div style={s.modalOverlay} onClick={closePreview}>
          <div style={s.previewCard} onClick={(e) => e.stopPropagation()}>
            <div style={s.previewHeader}>
              <div>
                <div style={s.previewTitle}>
                  {activeDoc.icon} {activeDoc.title} — {activeCompany.display}
                </div>
                <div style={s.previewSub}>Ye sirf preview hai, mail nahi gaya</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <a
                  href={previewUrl}
                  download={`${(form.name || "Document").replace(/\s+/g, "_")}_${docType}.pdf`}
                  style={{ ...s.btnDownload, background: brand }}
                >
                  ⬇ Download
                </a>
                <button style={s.btnClosePreview} onClick={closePreview}>✕ Close</button>
              </div>
            </div>
            <iframe title="PDF Preview" src={previewUrl} style={s.previewFrame} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Small field wrapper ──────────────────────────────────
function Field({ label, hint, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
      <label style={s.label}>{label}</label>
      {children}
      {hint && <span style={s.hint}>{hint}</span>}
    </div>
  );
}

// ── Styles (admin panel theme se match) ──────────────────
const s = {
  // company bar
  companyBar: {
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12,
    padding: "16px 18px", marginBottom: 16,
  },
  companyLabel: { fontSize: 13, fontWeight: 700, color: "#1e1b4b", marginBottom: 10 },
  companyBtns: { display: "flex", gap: 8, flexWrap: "wrap" },
  companyBtn: {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "9px 18px", borderRadius: 20, border: "1.5px solid #e2e8f0",
    background: "#fff", color: "#475569", fontSize: 13,
    fontWeight: 600, cursor: "pointer",
  },
  companyDot: {
    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
  },
  companyHint: { fontSize: 11, color: "#94a3b8", marginTop: 10 },

  cardRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 20,
  },
  docCard: {
    background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 12,
    padding: "16px 14px", textAlign: "left", cursor: "pointer",
    transition: "all .15s",
  },
  docCardActive: {
    background: ACCENT, border: `1.5px solid ${ACCENT}`,
    boxShadow: "0 4px 14px rgba(255,84,33,.3)",
  },
  docIcon: { fontSize: 26, marginBottom: 6 },
  docTitle: { fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 3 },
  docDesc: { fontSize: 12, color: "#64748b", lineHeight: 1.4 },

  formCard: {
    background: "#fff", borderRadius: 12,
    border: "1px solid #e2e8f0", padding: "22px 24px",
  },
  formHeader: {
    display: "flex", gap: 12, alignItems: "center",
    marginBottom: 18, paddingBottom: 14, borderBottom: "1px solid #f1f5f9",
  },
  formTitle: { fontSize: 17, fontWeight: 700, color: "#1e1b4b" },
  formSub: { fontSize: 12, color: "#64748b", marginTop: 2 },
  companyChip: {
    color: "#fff", border: "none",
    padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
    whiteSpace: "nowrap",
  },

  grid: { display: "flex", gap: 14, marginBottom: 4, flexWrap: "wrap" },
  grid4: { display: "flex", gap: 10, marginBottom: 4, flexWrap: "wrap" },

  label: { fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 },
  hint: { fontSize: 11, color: "#94a3b8", marginTop: -8, marginBottom: 10 },
  input: {
    width: "100%", padding: "11px 13px", borderRadius: 10,
    border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 12,
    boxSizing: "border-box", outline: "none", fontFamily: "inherit",
  },

  toggleRow: { display: "flex", gap: 8, marginBottom: 12 },
  toggleBtn: {
    flex: 1, padding: "10px 12px", borderRadius: 10,
    border: "1.5px solid #e2e8f0", background: "#fff",
    color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer",
    whiteSpace: "nowrap",
  },
  toggleBtnActive: {
    background: "#fff5f2", border: `1.5px solid ${ACCENT}`, color: ACCENT,
  },

  sectionLabel: {
    fontSize: 12, fontWeight: 700, color: "#94a3b8",
    textTransform: "uppercase", letterSpacing: ".5px",
    margin: "8px 0 10px",
  },

  // experience letter — quick template chips
  tplRow: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 },
  tplChip: {
    padding: "7px 14px", borderRadius: 20, border: "1.5px solid #e2e8f0",
    background: "#fff", color: "#475569", fontSize: 12.5,
    fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
  },

  advToggle: {
    background: "none", border: "none", color: ACCENT,
    fontSize: 13, fontWeight: 600, cursor: "pointer",
    padding: "8px 0", marginTop: 4,
  },
  advBox: {
    background: "#f8fafc", borderRadius: 10,
    padding: "14px 16px 2px", marginBottom: 8,
  },

  resultBanner: {
    borderRadius: 10, padding: "12px 16px", fontSize: 14,
    fontWeight: 600, marginTop: 6, marginBottom: 4,
  },
  resultOk: { background: "#d1fae5", border: "1px solid #6ee7b7", color: "#065f46" },
  resultErr: { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626" },

  actionRow: { display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" },
  btnReset: {
    padding: "12px 24px", background: "#f1f5f9", color: "#475569",
    border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  btnHtml: {
    padding: "12px 18px", background: "#f8fafc", color: "#475569",
    border: "1.5px solid #e2e8f0", borderRadius: 10,
    fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  btnPreview: {
    padding: "12px 24px", background: "#fff",
    borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer",
  },
  btnSend: {
    flex: 1, minWidth: 200, padding: "12px", color: "#fff",
    border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer",
  },
  tip: { fontSize: 12, color: "#94a3b8", marginTop: 12, lineHeight: 1.6 },

  // preview modal
  modalOverlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 200, padding: 16,
  },
  previewCard: {
    background: "#fff", borderRadius: 14, width: 900, maxWidth: "100%",
    height: "90vh", display: "flex", flexDirection: "column", overflow: "hidden",
    boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
  },
  previewHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 20px", borderBottom: "1px solid #e2e8f0", gap: 12,
  },
  previewTitle: { fontSize: 16, fontWeight: 700, color: "#1e1b4b" },
  previewSub: { fontSize: 12, color: "#64748b", marginTop: 2 },
  previewFrame: { flex: 1, width: "100%", border: "none" },
  btnDownload: {
    color: "#fff", padding: "9px 18px",
    borderRadius: 8, fontSize: 13, fontWeight: 700,
    textDecoration: "none", whiteSpace: "nowrap",
  },
  btnClosePreview: {
    background: "#f1f5f9", color: "#475569", border: "none",
    padding: "9px 18px", borderRadius: 8, fontSize: 13,
    fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
  },
};